import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { BookOpen, Flame, Sparkles, Heart } from "lucide-react";

// Group genres by vibe for a cleaner chip row
const GROUPS = [
  {
    label: "Steamy",
    icon: Flame,
    items: [
      "Dark Romance", "Mafia Romance", "Monster Romance", "Beastkin Romance",
      "Billionaire", "Enemies-to-Lovers", "Why Choose", "Slow Burn", "Age Gap",
    ],
  },
  {
    label: "Classics",
    icon: BookOpen,
    items: [
      "Romance", "Fantasy", "Sci-Fi", "Mystery", "Thriller", "Horror",
      "Contemporary", "Historical", "Young Adult", "LGBTQ+",
    ],
  },
];

export default function Library() {
  const [search, setSearch] = useSearchParams();
  const activeGenre = search.get("genre") || "";
  const [genres, setGenres] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function loadGenres(attempt = 0) {
      try {
        const r = await api.get("/genres");
        if (!cancelled) setGenres(r.data || []);
      } catch (e) {
        if (cancelled) return;
        if (attempt === 0) return loadGenres(1);
      }
    }
    loadGenres();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 60 };
    if (activeGenre) params.genre = activeGenre;
    async function loadStories(attempt = 0) {
      try {
        const r = await api.get("/stories", { params });
        if (cancelled) return;
        setStories(r.data || []);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        if (attempt === 0) return loadStories(1);
        setStories([]);
        setLoading(false);
      }
    }
    loadStories();
    return () => { cancelled = true; };
  }, [activeGenre]);

  const countByGenre = useMemo(() => {
    const map = {};
    for (const g of genres) map[g.name] = g.count;
    return map;
  }, [genres]);

  const pickGenre = (name) => {
    if (!name || name === activeGenre) {
      setSearch({});
    } else {
      setSearch({ genre: name });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">
          Library · browse by genre
        </div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">
          Find your next obsession.
        </h1>
        <p className="mt-3 text-slate-400 max-w-2xl">
          Every genre a reader loves — curated from steamy favourites to the classics.
          Tap a chip to filter.
        </p>
      </div>

      {GROUPS.map(({ label, icon: Icon, items }) => (
        <div key={label} className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
            <Icon className="h-3 w-3 text-amber-400" /> {label}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid={`genre-chip-all-${label.toLowerCase()}`}
              onClick={() => pickGenre("")}
              className={`inline-flex items-center gap-1 rounded-full border h-8 px-3 text-[11px] font-mono uppercase tracking-widest transition-colors ${
                !activeGenre
                  ? "bg-amber-500 border-amber-400 text-black"
                  : "bg-[#07080A] border-white/10 text-slate-400 hover:border-amber-400/60 hover:text-amber-300"
              }`}
            >
              All
            </button>
            {items.map((g) => {
              const active = activeGenre === g;
              const count = countByGenre[g] ?? 0;
              return (
                <button
                  key={g}
                  data-testid={`genre-chip-${g.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => pickGenre(g)}
                  className={`inline-flex items-center gap-1.5 rounded-full border h-8 px-3 text-[11px] font-mono uppercase tracking-widest transition-colors ${
                    active
                      ? "bg-amber-500 border-amber-400 text-black"
                      : "bg-[#07080A] border-white/10 text-slate-400 hover:border-amber-400/60 hover:text-amber-300"
                  }`}
                >
                  {g}
                  <span className={active ? "text-black/60" : "text-slate-600"}>· {count}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="mt-10">
        {loading ? (
          <div className="text-slate-500 text-sm">Loading stories…</div>
        ) : stories.length === 0 ? (
          <div
            data-testid="library-empty"
            className="rounded-3xl border border-white/10 bg-[#0F1117] p-10 text-center"
          >
            <Sparkles className="h-8 w-8 text-amber-400 mx-auto mb-3" />
            <div className="font-serif text-xl text-white mb-1">
              Nothing here yet for{" "}
              <span className="text-amber-300">{activeGenre || "this filter"}</span>
            </div>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              Be the first — hop into the AI story studio and spin something steamy.
            </p>
            <Link
              to="/create"
              data-testid="library-create-cta"
              className="inline-flex items-center gap-2 mt-5 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11 px-6"
            >
              <BookOpen className="h-4 w-4" /> Write a new story
            </Link>
          </div>
        ) : (
          <div
            data-testid="library-grid"
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {stories.map((s) => (
              <Link
                key={s.story_id}
                to={`/story/${s.story_id}`}
                data-testid={`library-story-${s.story_id}`}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0F1117] hover:border-amber-400/40 transition-colors flex flex-col"
              >
                <div className="aspect-[3/4] overflow-hidden bg-black/50 relative">
                  {s.cover_url ? (
                    <img
                      src={s.cover_url}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-700">
                      <BookOpen className="h-10 w-10" />
                    </div>
                  )}
                  {s.primary_genre && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-black/70 backdrop-blur px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-amber-200">
                      {s.primary_genre}
                    </span>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-serif text-lg text-white group-hover:text-amber-300 transition-colors line-clamp-2">
                    {s.title}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2 italic">
                    {s.tagline || "An interactive AI-crafted tale."}
                  </p>
                  {s.chapter_art_previews?.length > 0 && (
                    <div
                      data-testid={`chapter-art-previews-${s.story_id}`}
                      className="mt-3 flex items-center gap-1.5"
                      title={`${s.chapter_art_previews.length} chapter painting${s.chapter_art_previews.length === 1 ? "" : "s"} inside`}
                    >
                      {s.chapter_art_previews.slice(0, 4).map((url, i) => (
                        <img
                          key={i}
                          src={fileUrl(url)}
                          alt=""
                          loading="lazy"
                          className="h-10 w-10 rounded-md object-cover border border-white/10 group-hover:border-amber-400/40 transition-colors"
                        />
                      ))}
                      {s.chapter_art_previews.length > 4 && (
                        <span className="font-mono text-[10px] text-slate-500">
                          +{s.chapter_art_previews.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-auto pt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    <span className="truncate">by {s.author_name}</span>
                    <span className="inline-flex items-center gap-1 text-rose-300">
                      <Heart className="h-3 w-3" /> {s.votes || 0}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
